import React from 'react';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { createRun, deleteRun, listRuns, publishRun, getRun, Run } from '../run-outstanding/api';
import { Upload, FileSpreadsheet, X, Trash2, Send, AlertCircle, Loader2 } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "components/ui/dialog";
import dayjs from 'dayjs';
import { cn } from 'lib/utils';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

type FileUploadState = {
    ledger_file: File | null;
    cn_profitability_file: File | null;
    cit_file: File | null;
    disregard_entries_file: File | null;
    inter_division_txn_file: File | null;
    delivery_transhipment_file: File | null;
};

const FILE_LABELS: Record<keyof FileUploadState, string> = {
    ledger_file: 'Ledger',
    cn_profitability_file: 'CN Profitability',
    cit_file: 'CIT',
    disregard_entries_file: 'Disregard Ledger Entries',
    inter_division_txn_file: 'Inter-Division Transactions',
    delivery_transhipment_file: 'Delivery/Transhipment',
};

const PnlRunsPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const [selectedMonth, setSelectedMonth] = React.useState('');
    const [selectedYear, setSelectedYear] = React.useState('');
    const [files, setFiles] = React.useState<FileUploadState>({
        ledger_file: null,
        cn_profitability_file: null,
        cit_file: null,
        disregard_entries_file: null,
        inter_division_txn_file: null,
        delivery_transhipment_file: null,
    });
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState('');
    const [uploadSuccess, setUploadSuccess] = React.useState('');

    const [runs, setRuns] = React.useState<Run[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [actionLoading, setActionLoading] = React.useState<Record<string, boolean>>({});

    const [selectedErrorRun, setSelectedErrorRun] = React.useState<Run | null>(null);
    const [selectedPublishRun, setSelectedPublishRun] = React.useState<Run | null>(null);

    const fileInputRefs = React.useRef<Record<keyof FileUploadState, HTMLInputElement | null>>({
        ledger_file: null,
        cn_profitability_file: null,
        cit_file: null,
        disregard_entries_file: null,
        inter_division_txn_file: null,
        delivery_transhipment_file: null,
    });

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Settings', href: '#' },
            { label: 'Run Monthly Financials Report' },
        ]);
    }, [setBreadcrumbs]);

    const fetchRuns = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await listRuns('pnl');
            // Sort by created_at desc
            const sorted = [...data.runs].sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setRuns(sorted);
        } catch (error) {
            console.error('Failed to fetch runs:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    React.useEffect(() => {
        const activeRuns = runs.filter(run => run.status === 'running' || run.status === 'queued');
        if (activeRuns.length === 0) return;

        const poll = async () => {
            const updates = await Promise.all(
                activeRuns.map(async (run) => {
                    try {
                        const updatedRun = await getRun(run.id);
                        if (updatedRun.status !== run.status) {
                            return updatedRun;
                        }
                    } catch (e) {
                        console.error(`Failed to poll run ${run.id}`, e);
                    }
                    return null;
                })
            );

            const runsToUpdate = updates.filter((r): r is Run => r !== null);
            if (runsToUpdate.length > 0) {
                setRuns(prevRuns => prevRuns.map(run => {
                    const updated = runsToUpdate.find(u => u.id === run.id);
                    return updated || run;
                }));
            }
        };

        const intervalId = setInterval(poll, 1000);
        return () => clearInterval(intervalId);
    }, [runs]);

    const getFinancialYear = (year: number, month: number): string => {
        // month is 1-indexed (1 = Jan, 4 = April)
        if (month >= 4) { // April onwards
            return `${year}-${year + 1}`;
        } else {
            return `${year - 1}-${year}`;
        }
    };

    const handleRun = async () => {
        setUploadError('');
        setUploadSuccess('');

        if (!selectedMonth || !selectedYear) {
            setUploadError('Please select both month and year');
            return;
        }

        // Check if all files are uploaded
        const missingFiles: string[] = [];
        Object.entries(files).forEach(([key, file]) => {
            if (!file) {
                missingFiles.push(FILE_LABELS[key as keyof FileUploadState]);
            }
        });

        if (missingFiles.length > 0) {
            setUploadError(`Please upload all required files. Missing: ${missingFiles.join(', ')}`);
            return;
        }

        setUploading(true);
        try {
            const month = parseInt(selectedMonth);
            const year = parseInt(selectedYear);
            const fy = getFinancialYear(year, month);

            const formData = new FormData();
            formData.append('pipeline', 'pnl');
            formData.append('year', fy);
            formData.append('month', month.toString());

            // Append all files
            Object.entries(files).forEach(([key, file]) => {
                if (file) {
                    formData.append(key, file);
                }
            });

            const response = await createRun(formData);
            setUploadSuccess(`Run created successfully!`);

            // Reset form
            setSelectedMonth('');
            setSelectedYear('');
            setFiles({
                ledger_file: null,
                cn_profitability_file: null,
                cit_file: null,
                disregard_entries_file: null,
                inter_division_txn_file: null,
                delivery_transhipment_file: null,
            });
            Object.values(fileInputRefs.current).forEach(ref => {
                if (ref) ref.value = '';
            });

            // Refresh runs list
            fetchRuns();
        } catch (error: any) {
            setUploadError(error.message || 'Failed to create run');
        } finally {
            setUploading(false);
        }
    };

    const openPublishDialog = (run: Run) => {
        setSelectedPublishRun(run);
    };

    const handlePublishConfirm = async () => {
        if (!selectedPublishRun) return;

        const runId = selectedPublishRun.id;
        setActionLoading(prev => ({ ...prev, [runId]: true }));
        try {
            await publishRun(runId);
            fetchRuns();
            setSelectedPublishRun(null);
        } catch (error: any) {
            alert(`Failed to publish run: ${error.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [runId]: false }));
        }
    };

    const handleDelete = async (runId: string) => {
        if (!window.confirm('Are you sure you want to delete this run?')) {
            return;
        }

        setActionLoading(prev => ({ ...prev, [runId]: true }));
        try {
            await deleteRun(runId);
            fetchRuns();
        } catch (error: any) {
            console.error(error);
            alert(`Failed to delete run: ${error.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [runId]: false }));
        }
    };

    const handleFileChange = (fileKey: keyof FileUploadState) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];

            if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
                setUploadError(`${FILE_LABELS[fileKey]} must be a valid Excel file (.xlsx or .xls)`);
                setFiles(prev => ({ ...prev, [fileKey]: null }));
                if (fileInputRefs.current[fileKey]) fileInputRefs.current[fileKey]!.value = '';
                return;
            }

            setFiles(prev => ({ ...prev, [fileKey]: selectedFile }));
            setUploadError('');
        }
    };

    const removeFile = (fileKey: keyof FileUploadState) => {
        setFiles(prev => ({ ...prev, [fileKey]: null }));
        if (fileInputRefs.current[fileKey]) fileInputRefs.current[fileKey]!.value = '';
    };

    // Generate month options
    const monthOptions = [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
    ];

    // Generate year options (current year and 2 years back)
    const currentYear = new Date().getFullYear();
    const yearOptions = [
        { value: currentYear.toString(), label: currentYear.toString() },
        { value: (currentYear - 1).toString(), label: (currentYear - 1).toString() },
        { value: (currentYear - 2).toString(), label: (currentYear - 2).toString() },
    ];

    const allFilesUploaded = Object.values(files).every(file => file !== null);

    return (
        <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-6 sm:px-8 py-6">
            {/* Upload Section */}
            <div>
                <Card className="border-border">
                    <CardHeader className="pb-3 px-6 pt-5">
                        <CardTitle className="text-lg font-semibold flex items-center gap-2">
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            Upload P&L Report Files
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6 space-y-4">
                        {/* Month and Year Selection */}
                        <div className="flex items-start gap-4">
                            <div className="flex-1 max-w-[200px]">
                                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                                    Month
                                </label>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={uploading}
                                >
                                    <option value="">Select month...</option>
                                    {monthOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex-1 max-w-[200px]">
                                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                                    Year
                                </label>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={uploading}
                                >
                                    <option value="">Select year...</option>
                                    {yearOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* File Uploads */}
                        <div className="space-y-3">
                            <label className="text-sm font-medium text-muted-foreground block">
                                Upload Files
                            </label>
                            <div className="grid grid-cols-3 gap-3">
                                {(Object.keys(files) as Array<keyof FileUploadState>).map((fileKey) => (
                                    <div key={fileKey}>
                                        {!files[fileKey] ? (
                                            <div
                                                onClick={() => fileInputRefs.current[fileKey]?.click()}
                                                className="h-10 border border-dashed border-input rounded-md flex items-center px-4 cursor-pointer hover:bg-accent/50 transition-colors text-sm text-muted-foreground"
                                            >
                                                <FileSpreadsheet className="h-4 w-4 mr-2" />
                                                {FILE_LABELS[fileKey]}...
                                            </div>
                                        ) : (
                                            <div className="h-10 border border-dashed border-blue-200 bg-blue-50/50 rounded-md flex items-center justify-between px-3 text-sm">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <FileSpreadsheet className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                                    <span className="truncate font-medium text-blue-700">{files[fileKey]!.name}</span>
                                                </div>
                                                <button
                                                    onClick={() => removeFile(fileKey)}
                                                    className="ml-2 p-1 hover:bg-blue-100 rounded-full text-blue-600 transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        <input
                                            ref={(el) => { fileInputRefs.current[fileKey] = el; }}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleFileChange(fileKey)}
                                            className="hidden"
                                            disabled={uploading}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Run Button */}
                        <div className="flex justify-end pt-2">
                            <Button
                                onClick={handleRun}
                                disabled={uploading || !allFilesUploaded || !selectedMonth || !selectedYear}
                                className="h-10 min-w-[100px]"
                            >
                                {uploading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                    'Run'
                                )}
                            </Button>
                        </div>

                        {uploadError && (
                            <p className="text-sm text-destructive mt-3">{uploadError}</p>
                        )}
                        {uploadSuccess && (
                            <p className="text-sm text-green-600 mt-3">{uploadSuccess}</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Previous Runs Section */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold tracking-tight">Previous Runs</h2>
                <div className="rounded-md border bg-card">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead className="w-[180px]">Period</TableHead>
                                <TableHead>Financial Year</TableHead>
                                <TableHead>Created By</TableHead>
                                <TableHead>Created At</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead className="text-right w-[220px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="h-24 text-center">
                                        <div className="flex items-center justify-center text-muted-foreground">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2" />
                                            Loading runs...
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : runs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                                        No runs found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                runs.map((run) => (
                                    <TableRow key={run.id}>
                                        <TableCell className="font-medium">
                                            {dayjs().month(run.month - 1).format('MMMM')} {run.year.split('-')[0]}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {run.year}
                                        </TableCell>
                                        <TableCell>
                                            {run.created_by}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {dayjs(run.created_at).format('MMM D, YYYY h:mm A')}
                                        </TableCell>
                                        <TableCell>
                                            <span className={cn(
                                                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                                                run.status === 'succeeded' ? "bg-green-50 text-green-700 ring-green-600/20" :
                                                    run.status === 'running' ? "bg-blue-50 text-blue-700 ring-blue-600/20" :
                                                        run.status === 'queued' ? "bg-yellow-50 text-yellow-700 ring-yellow-600/20" :
                                                            "bg-red-50 text-red-700 ring-red-600/20"
                                            )}>
                                                {(run.status === 'running' || run.status === 'queued') && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                                                {run.status === 'succeeded' ? 'Succeeded' :
                                                    run.status === 'running' ? 'Running' :
                                                        run.status === 'queued' ? 'Queued' :
                                                            'Failed'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={cn(
                                                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                                                run.state === 'published' ? "bg-purple-50 text-purple-700 ring-purple-600/20" :
                                                    run.state === 'deleted' ? "bg-gray-50 text-gray-700 ring-gray-600/20" :
                                                        "bg-amber-50 text-amber-700 ring-amber-600/20"
                                            )}>
                                                {run.state === 'published' ? 'Published' :
                                                    run.state === 'deleted' ? 'Deleted' : 'Draft'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2 min-h-[28px]">
                                                {run.status === 'failed' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs border-destructive text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5 px-2.5"
                                                        onClick={() => setSelectedErrorRun(run)}
                                                    >
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        View Error Log
                                                    </Button>
                                                )}

                                                {run.status === 'succeeded' && run.state === 'draft' && (
                                                    <>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 text-xs gap-1.5 px-2.5 border-green-200 text-green-700 hover:text-green-800 hover:bg-green-50 hover:border-green-300"
                                                            onClick={() => openPublishDialog(run)}
                                                            disabled={actionLoading[run.id]}
                                                        >
                                                            <Send className="h-3 w-3" />
                                                            Publish
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 text-xs gap-1.5 px-2.5 border-red-200 text-red-700 hover:text-red-800 hover:bg-red-50 hover:border-red-300"
                                                            onClick={() => handleDelete(run.id)}
                                                            disabled={actionLoading[run.id]}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                            Delete
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={!!selectedErrorRun} onOpenChange={(open) => !open && setSelectedErrorRun(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="h-5 w-5" />
                            Run Failed
                        </DialogTitle>
                        <DialogDescription>
                            Run ID: {selectedErrorRun?.id}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 bg-muted/50 rounded-md p-4 max-h-[400px] overflow-y-auto">
                        <pre className="text-sm font-mono whitespace-pre-wrap text-foreground">
                            {selectedErrorRun?.error || 'No error details available.'}
                        </pre>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!selectedPublishRun} onOpenChange={(open) => !open && setSelectedPublishRun(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Publish</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to publish this run? Once published, the data will be visible to all users.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedPublishRun(null)}>
                            Cancel
                        </Button>
                        <Button onClick={handlePublishConfirm} disabled={!!selectedPublishRun && actionLoading[selectedPublishRun.id]}>
                            {!!selectedPublishRun && actionLoading[selectedPublishRun.id] ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Publishing...
                                </>
                            ) : (
                                'Publish'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PnlRunsPage;
